export type RegionKey = "north" | "center" | "mediterranean" | "south_canary";

export type DayUse = "low" | "medium" | "high";

export type RoofType = "flat" | "pitched" | "unknown";

export interface EstimatorInput {
  province: string;
  region: RegionKey;
  annualConsumptionKwh?: number;
  monthlyBillEuro?: number;
  roofAreaM2: number;
  roofType: RoofType;
  dayUse: DayUse;
  shadingPercent: number;
  wantsBattery: boolean;
  electricityPriceEuroKwh?: number;
  exportPriceEuroKwh?: number;
}

export interface Range {
  low: number;
  high: number;
}

export interface SolarEstimate {
  annualConsumptionKwh: Range;
  recommendedKwp: Range;
  panels: Range;
  annualProductionKwh: Range;
  investmentEuro: Range;
  annualSavingsEuro: Range;
  paybackYears: Range;
  roofLimited: boolean;
  selfConsumptionRate: Range;
  co2AvoidedKg: Range;
  assumptions: string[];
  warnings: string[];
}

export const REGION_PROFILES: Record<RegionKey, { label: string; yieldKwhPerKwp: Range }> = {
  north: {
    label: "Norte / cornisa cantábrica",
    yieldKwhPerKwp: { low: 1050, high: 1250 }
  },
  center: {
    label: "Centro peninsular",
    yieldKwhPerKwp: { low: 1350, high: 1550 }
  },
  mediterranean: {
    label: "Mediterráneo / valle del Ebro",
    yieldKwhPerKwp: { low: 1450, high: 1650 }
  },
  south_canary: {
    label: "Sur peninsular / Canarias",
    yieldKwhPerKwp: { low: 1550, high: 1800 }
  }
};

const PRICE_RANGE_EURO_KWH: Range = { low: 0.18, high: 0.28 };
const EXPORT_PRICE_RANGE_EURO_KWH: Range = { low: 0.035, high: 0.08 };
const RESIDENTIAL_INSTALLED_COST_EURO_KWP: Range = { low: 900, high: 1400 };
const RESIDENTIAL_MIN_PROJECT_COST_EURO: Range = { low: 3200, high: 4500 };
const BATTERY_EXTRA_COST_EURO: Range = { low: 3600, high: 7800 };
const PANEL_WP = 455;
const EFFECTIVE_KWP_PER_M2 = 0.16;
const CO2_KG_PER_KWH = 0.18;

const daytimeUseToSelfConsumption: Record<DayUse, Range> = {
  low: { low: 0.34, high: 0.48 },
  medium: { low: 0.46, high: 0.62 },
  high: { low: 0.58, high: 0.74 }
};

function roundTo(value: number, step: number): number {
  return Number((Math.round(value / step) * step).toFixed(4));
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

function multiplyRange(a: Range, b: Range): Range {
  return { low: a.low * b.low, high: a.high * b.high };
}

function divideRange(a: Range, b: Range): Range {
  return { low: a.low / b.high, high: a.high / b.low };
}

function normalizeRange(range: Range): Range {
  return {
    low: Math.min(range.low, range.high),
    high: Math.max(range.low, range.high)
  };
}

export function estimateAnnualConsumption(input: EstimatorInput): Range {
  if (input.annualConsumptionKwh && input.annualConsumptionKwh > 0) {
    return {
      low: input.annualConsumptionKwh * 0.92,
      high: input.annualConsumptionKwh * 1.08
    };
  }

  if (input.monthlyBillEuro && input.monthlyBillEuro > 0) {
    const price = input.electricityPriceEuroKwh
      ? { low: input.electricityPriceEuroKwh * 0.9, high: input.electricityPriceEuroKwh * 1.1 }
      : PRICE_RANGE_EURO_KWH;
    return normalizeRange({
      low: (input.monthlyBillEuro * 12) / price.high,
      high: (input.monthlyBillEuro * 12) / price.low
    });
  }

  return { low: 2800, high: 5200 };
}

export function estimateSolar(input: EstimatorInput): SolarEstimate {
  const annualConsumptionKwh = estimateAnnualConsumption(input);
  const profile = REGION_PROFILES[input.region];
  const shadeFactor: Range = {
    low: clamp(1 - input.shadingPercent / 100 - 0.04, 0.45, 1),
    high: clamp(1 - input.shadingPercent / 100 + 0.04, 0.45, 1)
  };
  const roofMultiplier = input.roofType === "flat" ? 0.88 : input.roofType === "unknown" ? 0.82 : 1;
  const roofMaxKwp = Math.max(0.6, input.roofAreaM2 * EFFECTIVE_KWP_PER_M2 * roofMultiplier);

  const targetCoverage = input.wantsBattery ? { low: 0.72, high: 0.98 } : { low: 0.55, high: 0.82 };
  const rawTargetKwp = divideRange(multiplyRange(annualConsumptionKwh, targetCoverage), profile.yieldKwhPerKwp);
  const recommendedKwp: Range = {
    low: clamp(roundTo(rawTargetKwp.low * 0.9, 0.1), 1.4, roofMaxKwp),
    high: clamp(roundTo(rawTargetKwp.high * 1.12, 0.1), 1.8, roofMaxKwp)
  };
  if (recommendedKwp.high < recommendedKwp.low) {
    recommendedKwp.high = recommendedKwp.low;
  }

  const panelRange = {
    low: Math.max(4, Math.ceil((recommendedKwp.low * 1000) / PANEL_WP)),
    high: Math.max(4, Math.ceil((recommendedKwp.high * 1000) / PANEL_WP))
  };

  const productionBeforeShade = multiplyRange(recommendedKwp, profile.yieldKwhPerKwp);
  const annualProductionKwh = multiplyRange(productionBeforeShade, shadeFactor);

  const selfConsumptionBase = daytimeUseToSelfConsumption[input.dayUse];
  const batteryBoost = input.wantsBattery ? 0.18 : 0;
  const selfConsumptionRate = {
    low: clamp(selfConsumptionBase.low + batteryBoost, 0.28, 0.9),
    high: clamp(selfConsumptionBase.high + batteryBoost, 0.4, 0.92)
  };

  const importPrice = input.electricityPriceEuroKwh
    ? { low: input.electricityPriceEuroKwh * 0.92, high: input.electricityPriceEuroKwh * 1.08 }
    : PRICE_RANGE_EURO_KWH;
  const exportPrice = input.exportPriceEuroKwh
    ? { low: input.exportPriceEuroKwh * 0.75, high: input.exportPriceEuroKwh * 1.25 }
    : EXPORT_PRICE_RANGE_EURO_KWH;

  const selfConsumedKwh = {
    low: Math.min(annualProductionKwh.low * selfConsumptionRate.low, annualConsumptionKwh.high),
    high: Math.min(annualProductionKwh.high * selfConsumptionRate.high, annualConsumptionKwh.low * 1.05)
  };
  const exportedKwh = {
    low: Math.max(0, annualProductionKwh.low - selfConsumedKwh.high),
    high: Math.max(0, annualProductionKwh.high - selfConsumedKwh.low)
  };

  const directSavings = multiplyRange(selfConsumedKwh, importPrice);
  const exportCompensation = multiplyRange(exportedKwh, exportPrice);
  const annualSavingsEuro = normalizeRange({
    low: directSavings.low + exportCompensation.low * 0.75,
    high: directSavings.high + exportCompensation.high * 0.95
  });

  const variableInvestment = multiplyRange(recommendedKwp, RESIDENTIAL_INSTALLED_COST_EURO_KWP);
  const baseInvestment = {
    low: Math.max(variableInvestment.low, RESIDENTIAL_MIN_PROJECT_COST_EURO.low),
    high: Math.max(variableInvestment.high, RESIDENTIAL_MIN_PROJECT_COST_EURO.high)
  };
  const investmentEuro = {
    low: baseInvestment.low + (input.wantsBattery ? BATTERY_EXTRA_COST_EURO.low : 0),
    high: baseInvestment.high + (input.wantsBattery ? BATTERY_EXTRA_COST_EURO.high : 0)
  };

  const conservativeSmallSystemCost =
    Math.max(variableInvestment.low, RESIDENTIAL_MIN_PROJECT_COST_EURO.high) +
    (input.wantsBattery ? BATTERY_EXTRA_COST_EURO.high : 0);
  const paybackYears = normalizeRange({
    low: investmentEuro.low / Math.max(annualSavingsEuro.high, 1),
    high: Math.max(
      conservativeSmallSystemCost / Math.max(annualSavingsEuro.low, 1),
      investmentEuro.high / Math.max(annualSavingsEuro.high, 1)
    )
  });

  const roofLimited = rawTargetKwp.high > roofMaxKwp;
  const warnings = [
    roofLimited ? "El tejado parece limitar la potencia útil: conviene revisión técnica." : "",
    input.shadingPercent >= 25 ? "La sombra indicada puede reducir bastante la rentabilidad." : "",
    input.wantsBattery ? "La batería puede subir autoconsumo, pero aumenta inversión y debe dimensionarse con curvas horarias." : "",
    "La compensación de excedentes depende de contrato, horas y normativa aplicable; no se garantiza importe."
  ].filter(Boolean);

  return {
    annualConsumptionKwh: {
      low: Math.round(annualConsumptionKwh.low),
      high: Math.round(annualConsumptionKwh.high)
    },
    recommendedKwp: {
      low: roundTo(recommendedKwp.low, 0.1),
      high: roundTo(recommendedKwp.high, 0.1)
    },
    panels: panelRange,
    annualProductionKwh: {
      low: Math.round(annualProductionKwh.low),
      high: Math.round(annualProductionKwh.high)
    },
    investmentEuro: {
      low: Math.round(investmentEuro.low / 100) * 100,
      high: Math.round(investmentEuro.high / 100) * 100
    },
    annualSavingsEuro: {
      low: Math.round(annualSavingsEuro.low / 10) * 10,
      high: Math.round(annualSavingsEuro.high / 10) * 10
    },
    paybackYears: {
      low: Number(paybackYears.low.toFixed(1)),
      high: Number(paybackYears.high.toFixed(1))
    },
    roofLimited,
    selfConsumptionRate: {
      low: Number((selfConsumptionRate.low * 100).toFixed(0)),
      high: Number((selfConsumptionRate.high * 100).toFixed(0))
    },
    co2AvoidedKg: {
      low: Math.round(selfConsumedKwh.low * CO2_KG_PER_KWH),
      high: Math.round(selfConsumedKwh.high * CO2_KG_PER_KWH)
    },
    assumptions: [
      `Producción estimada para ${profile.label}: ${profile.yieldKwhPerKwp.low}-${profile.yieldKwhPerKwp.high} kWh/kWp/año antes de sombra.`,
      `Coste residencial instalado usado: ${RESIDENTIAL_INSTALLED_COST_EURO_KWP.low}-${RESIDENTIAL_INSTALLED_COST_EURO_KWP.high} €/kWp, con mínimo de proyecto ${RESIDENTIAL_MIN_PROJECT_COST_EURO.low}-${RESIDENTIAL_MIN_PROJECT_COST_EURO.high} € antes de batería.`,
      `Retorno simple calculado con escenarios emparejados para evitar cruzar coste máximo con producción mínima incompatible.`,
      `Precio electricidad usado si no se introduce dato: ${PRICE_RANGE_EURO_KWH.low}-${PRICE_RANGE_EURO_KWH.high} €/kWh.`,
      `Compensación excedentes usada si no se introduce dato: ${EXPORT_PRICE_RANGE_EURO_KWH.low}-${EXPORT_PRICE_RANGE_EURO_KWH.high} €/kWh.`
    ],
    warnings
  };
}

export function formatEuro(value: number): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0
  }).format(value);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 1
  }).format(value);
}
